import type { GeoPoint } from '../geolocation';
import type { RoutingVehicleProfile } from '../routing/routing.interface';

/**
 * Freshness of a computed ETA figure.
 * - APPROXIMATE: computed from an action-based anchor (arrival, last position, completion).
 * - LIVE: computed from a continuous position feed (the `driverpositions` source) — reserved,
 *   not yet written.
 * - UNAVAILABLE: frozen because the anchor is too stale to trust (freeze semantics) — reserved,
 *   not yet written.
 */
export const ETA_FRESHNESS = {
  APPROXIMATE: 'approximate',
  LIVE: 'live',
  UNAVAILABLE: 'unavailable',
} as const;
export type EtaFreshness = (typeof ETA_FRESHNESS)[keyof typeof ETA_FRESHNESS];

/**
 * Source of the anchor (position + time) an ETA computation chain is rooted at — see the
 * ETA engine's anchor resolver (`apps/pharos-back-deliveries/src/eta/eta-anchor-resolver.ts`)
 * for resolution rules.
 * - ARRIVED_AT_STOP: a persisted queue stop's client-declared `arrivedAt`.
 * - COMPLETED_JOB: a persisted queue stop's client-declared `completedJobAt`/`failedJobAt`.
 * - LAST_POSITION: the driver's latest `driverpositions` record.
 */
export const ETA_ANCHOR_KIND = {
  ARRIVED_AT_STOP: 'arrived_at_stop',
  COMPLETED_JOB: 'completed_job',
  LAST_POSITION: 'last_position',
} as const;
export type EtaAnchorKind = (typeof ETA_ANCHOR_KIND)[keyof typeof ETA_ANCHOR_KIND];

/** The position + time an ETA computation chain is rooted at. */
export interface EtaAnchor {
  /** `departure(S0)` — the departure time of the chain. */
  departureTime: Date;
  kind: EtaAnchorKind;
  position: GeoPoint;
}

/**
 * A computed ETA figure, stamped identically on `Stop.eta` and `Order.eta` by the ETA engine in
 * `pharos-back-deliveries`. `Date` fields travel as ISO strings over HTTP/Service Bus — same trap
 * as `Order.updatedAt`.
 */
export interface Eta {
  anchor: EtaAnchor;
  /** When the engine applied the routing response that produced `value` — the figure's own timestamp. */
  computedAt: Date;
  /** When the computation that produced `value` was requested. */
  requestedAt: Date;
  state: EtaFreshness;
  /** The computed estimated time of arrival. */
  value: Date;
  vehicleProfile: RoutingVehicleProfile;
}
